import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { fetchCompanySelfDevelopmentUserDetail } from './companySelfDevelopmentApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  toneChip
} from '../../../../component/common/designSystem';
import { TOOL_COLORS, toolColor } from './selfDevelopmentTheme';

/**
 * Self Development → User profile.
 *
 * One person's practice across the six tools: how much they have written in
 * each, how many distinct days they showed up, what each tool's own
 * follow-through looks like for them, and when they were last here.
 *
 * THERE IS NO ENTRY LIST, and there is not going to be one. Every one of these
 * six collections stores writing that was never addressed to an employer — a
 * gratitude, a journal page, a thought record, a goal, an affirmation, a board.
 * The endpoint behind this page never reads any of it, so there is nothing here
 * to leak. Counts, dates and the actions the person took deliberately are the
 * whole of what belongs on a company dashboard.
 *
 * Days practised is the headline rather than entries written: somebody with
 * sixty entries over three days had three good afternoons, and somebody with
 * twenty over twenty days has a habit.
 *
 * `fetcher`/`backPath`/`backLabel` are props so an unscoped hub could reuse the
 * page unchanged.
 */
const SelfDevelopmentUserDetail = ({
  fetcher = fetchCompanySelfDevelopmentUserDetail,
  backPath,
  backLabel
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const tools = detail?.tools || [];
  const toolMix = detail?.toolMix || [];
  const entriesTrend = (detail?.dailyTrend || []).map((day) => day.entries || 0);

  const usedTools = tools.filter((row) => row.entries > 0);
  const trendHasData = entriesTrend.some((value) => value > 0);

  const toolMixColors = toolMix.map((row) => {
    const match = tools.find((tool) => tool.label === row.label);
    return toolColor(match?.key);
  });

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: lastNDaysLabels(entriesTrend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} entries` } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4 mt-6">
        {/* Distinct days first: it is the only one of these that says whether
            the habit stuck. */}
        <StatCard
          icon={STAT_ICONS.streak}
          title="Days Practised"
          value={detail ? formatBigNumber(detail.activeDays || 0) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Entries Written"
          value={user ? formatBigNumber(user.totalEntries) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Tools Used"
          value={user ? `${user.toolsUsed} of ${user.toolsAvailable}` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.newUsers}
          title="First Entry"
          value={user?.firstEntryDate || '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Last Entry"
          value={user?.lastEntryDate || '-'}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
        <MiniDonutCard
          title="Where Their Practice Goes"
          subtitle="Entries by tool"
          data={toolMix}
          colors={toolMixColors}
          unit="entries"
          totalLabel="Entries"
          legendLimit={6}
        />

        <Panel
          className="xl:col-span-2"
          title="Practice Over Time"
          subtitle="Entries across every tool — last 30 days"
        >
          {trendHasData ? (
            <Chart
              type="bar"
              height={300}
              width="100%"
              series={[{ name: 'Entries', data: entriesTrend }]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>Nothing written in the last 30 days.</PanelEmpty>
          )}
        </Panel>
      </div>

      <div className="mt-6">
        <Panel
          title="Tool by Tool"
          subtitle="What they have built, and what each tool asks of them"
        >
          {usedTools.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Tool</th>
                  <th className="py-2 px-4 font-medium text-center">Entries</th>
                  <th className="py-2 px-4 font-medium text-center">Days</th>
                  <th className="py-2 px-4 font-medium text-center">Follow-through</th>
                  <th className="py-2 px-4 font-medium text-center">First</th>
                  <th className="py-2 font-medium text-right">Last</th>
                </>
              }
            >
              {usedTools.map((row) => (
                <tr key={row.key} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: TOOL_COLORS[row.key] }}
                      />
                      {row.label}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.entries)}</td>
                  {/* Per-tool days. They do NOT add up to "Days Practised"
                      above — a goal and a journal entry on the same afternoon
                      is one day of practice, not two. */}
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.activeDays)}</td>
                  <td className="py-2 px-4 text-center">
                    {formatBigNumber(row.outcome)}{' '}
                    <span className="text-gray-400">{row.outcomeLabel.toLowerCase()}</span>
                  </td>
                  <td className="py-2 px-4 text-center">{row.firstDate || '-'}</td>
                  <td className="py-2 text-right">{row.lastDate || '-'}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>This user has not used any self-development tool yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

SelfDevelopmentUserDetail.propTypes = {
  fetcher: PropTypes.func,
  backPath: PropTypes.string,
  backLabel: PropTypes.string
};

export default SelfDevelopmentUserDetail;
